/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { getTermByKey } from "../../common/TranslateHelpers";
import { ConfirmationDialog } from "../../dialogs/ConfirmationDialog/ConfirmationDialog";
import { termsEditorCommon as terms, termsEditorCommon } from "../../terms/common";
import { TOAST_TYPE } from "../../toasts/IToast";
// import { DeployDialog } from "./DeployDialog/DeployDialog";
import { ImportBlueprintDialog } from "./ImportBlueprintDialog/ImportBlueprintDialog";
import { ResourceListDialog } from "./ResourceListDialog/ResourceListDialog";
import { RenameDialog } from "./RenameDialog/RenameDialog";
import { AppPreview } from "./AppPreview/AppPreview";
import { termsEditorViewEditor } from "../../terms/viewEditor";
import { RESOURCE_TYPES } from "@adapptio/lib-core";
import { generateId } from "@adapptio/lib-components/src/Functions/generateId";
import { InputDialog } from "../../dialogs/InputDialog/InputDialog";
import {
	ISchemaBuilderAny,
	ISchemaComponentListModel,
	ISchemaComponentModel,
	ISchemaConstMapModel,
	ISchemaConstMapModelEntry,
	ISchemaDataSourceModel,
	isModel,
	isSchemaComponentListModel,
	isSchemaConstArrayModel,
	SCHEMA_VALIDATION_ERROR_TYPE,
	TGenericBlueprintSchema,
	ValidatorString
} from "@adapptio/lib-blueprint";
import { normalizeResourcePath } from "../../resources/ResourceHelpers";
import { IEditor } from "../../IEditor";
import { CreateDirectoryDialog } from "./CreateDirectoryDialog/CreateDirectoryDialog";
import { isFunction } from "@adapptio/lib-shared";
import { ISchemaDataSourceListModel } from "@adapptio/lib-blueprint/src/schemas/SchemaDataSourceList";
import { isViewEditable } from "../../view/viewUtils";
import { ErrorTracker } from "@adapptio/lib-components";
import { ACTION_BUTTON_STYLE } from "../../ui/components/ActionButton";
import { ReleaseDialog } from "./ReleaseDialog/ReleaseDialog";
import { API_ERROR_NAME, ApiError } from "../../api/ApiErrors";

export function registerCoreCommands(dc: IEditor): void {
	const commandsSvc = dc.get("commandsService");
	const resourceSvc = dc.get("resourceService");
	const viewSvc = dc.get("viewService");
	const dialogSvc = dc.get("dialogService");
	const toastSvc = dc.get("toastService");
	const productSupportSvc = dc.get("productSupportService");
	const apiSvc = dc.get("apiService");

	commandsSvc.register({
		id: "duplicate.mapModel",
		label: "Duplicate Map model",
		args: [
			{
				label: "Map model",
				isOptional: false
			},
			{
				label: "Map entry",
				isOptional: false
			}
		],
		returns: "none",
		handler: async (args) => {
			const mapModel = args[0] as ISchemaConstMapModel<TGenericBlueprintSchema>;
			const mapEntry = args[1] as ISchemaConstMapModelEntry<ISchemaBuilderAny>;

			const idt = mapEntry.value.schema.serialize(mapEntry.value, [ "$" ]);
			const cloneValue = mapEntry.value.schema.parse(mapEntry.value.ctx, idt, null);
			const newId = mapEntry.key.value + "_copy";

			mapModel.schema.addElementModel(mapModel, newId, cloneValue, true);
		}
	});

	commandsSvc.register({
		id: "duplicate.dataSource",
		label: "Duplicate data source",
		args: [
			{
				label: "Model node",
				isOptional: false
			}
		],
		returns: "none",
		handler: async (args) => {
			const modelNode = args[0] as ISchemaDataSourceModel;

			const idt = modelNode.schema.serialize(modelNode, [ "$" ]);
			const cloneModelNode = modelNode.schema.parse(modelNode.ctx, idt, null);
			const newId = modelNode.ctx.getUniqueIdentifier(modelNode.id.value + "_copy", true);
			cloneModelNode.id.schema.setValue(cloneModelNode.id, newId, true);

			const targetContainerModel = modelNode.parentDataSourceList;

			targetContainerModel.schema.addItemModel(
				targetContainerModel as unknown as ISchemaDataSourceListModel,
				targetContainerModel.items.findIndex((item) => item.nodeId === cloneModelNode.nodeId) + 2,
				cloneModelNode,
				true
			);
		}
	});

	commandsSvc.register({
		id: "duplicate.component",
		label: "Duplicate component",
		args: [
			{
				label: "Model node",
				isOptional: false
			}
		],
		returns: "none",
		handler: async (args) => {
			const modelNode = args[0] as ISchemaComponentModel;

			const idt = modelNode.schema.serialize(modelNode, [ "$" ]);
			const cloneModelNode = modelNode.schema.parse(modelNode.ctx, idt, null);
			const newId = modelNode.ctx.getUniqueIdentifier(modelNode.id.value + "_copy", true);
			cloneModelNode.id.schema.setValue(cloneModelNode.id, newId, true);

			const targetContainerModel = isSchemaComponentListModel(cloneModelNode.parent)
				? cloneModelNode.parent
				: modelNode.parentComponentList;

			targetContainerModel.schema.addItemModel(
				targetContainerModel as unknown as ISchemaComponentListModel<Record<string, never>>,
				targetContainerModel.items.findIndex((item) => item.nodeId === modelNode.nodeId) + 1,
				cloneModelNode,
				true
			);
		}
	});

	commandsSvc.register({
		id: "duplicate.arrayItem",
		label: "Duplicate item",
		args: [
			{
				label: "Item model node",
				isOptional: false
			},
			{
				label: "Array model node",
				isOptional: false
			}
		],
		returns: "none",
		handler: async (args) => {
			const itemModel = args[0];
			const arrayModel = args[1];

			if (!isModel(itemModel)) {
				console.error("Not a model node", itemModel);
				return;
			}

			if (!isSchemaConstArrayModel(arrayModel)) {
				console.error("Not an array model node", arrayModel);
				return;
			}

			const index = arrayModel.items.findIndex((item) => item.nodeId === itemModel.nodeId);

			if (index < 0) {
				console.error("Model is not an item of this array model", itemModel, arrayModel);
				return;
			}

			const idt = itemModel.schema.serialize(itemModel, [ "$" ]);
			const clonedModelNode = itemModel.schema.parse(itemModel.ctx, idt, null);

			arrayModel.schema.addElementModel(arrayModel, clonedModelNode, index + 1, true);
		}
	});

	commandsSvc.register({
		id: "eventNode.deselect",
		label: "Deselect all event nodes",
		args: [],
		returns: "none",
		handler: async () => {
			const view = viewSvc.getCurrentView();

			if (isFunction(view.deselectAllEventNode)) {
				view.deselectAllEventNode();
			}
		}
	});

	// @todo translate
	commandsSvc.register({
		id: "url.open",
		label: "Open URL",
		args: [
			{
				label: "URL",
				isOptional: false,
				schema: {
					type: "string"
				}
			}
		],
		returns: "none",
		handler: async (args) => {
			window.open(args[0] as string);
		}
	});

	commandsSvc.register({
		id: "referenceGuide.open",
		label: "Open reference guide",
		args: [
			{
				label: "Select component",
				isOptional: false
			}
		],
		returns: "none",
		handler: async (args) => {
			const select = args[0];

			viewSvc.open("reference-guide", "reference-guide", { initSelected: select }, true);
		}
	});

	commandsSvc.register({
		id: "resource.create.directory",
		label: "Create a directory",
		args: [],
		returns: "none",
		handler: async () => {
			const res = await dialogSvc.open(CreateDirectoryDialog, "resource.createDirectory", {
				getValidators: ({ basePath = "" } = {}) => [
					ValidatorString({ required: true }),
					(name) => {
						if (name?.includes("/")) {
							return [
								{
									type: SCHEMA_VALIDATION_ERROR_TYPE.PATTERN,
									message: terms.dialog.createDirectory.messages.pattern,
									metaData: {}
								}
							];
						}

						const fullPath = normalizeResourcePath(basePath, name);
						if (resourceSvc.getResourceByPath(fullPath)) {
							return [
								{
									type: SCHEMA_VALIDATION_ERROR_TYPE.DUPLICATE_IDENTIFIER,
									message: terms.dialog.createDirectory.messages.duplicateIdentifier,
									metaData: {}
								}
							];
						}

						return [];
					}
				]
			});

			if (!res?.name) {
				return;
			}

			const { basePath, name } = res;

			const loadingToast = toastSvc.add({
				type: TOAST_TYPE.LOADING,
				message: termsEditorViewEditor.toasts.creatingDirectory,
				isClosable: false
			});

			try {
				await resourceSvc.createDirectory(name, basePath);
			} catch (err) {
				// @TODO: show different messages for different errors
				const errorTracker = ErrorTracker.getInstance();
				errorTracker.captureException(err);

				toastSvc.add({
					type: TOAST_TYPE.ERROR,
					message: termsEditorCommon.toasts.createDirectory.error.unknown,
					details: String(err)
				});
			} finally {
				toastSvc.remove(loadingToast);
			}

			return;
		}
	});

	commandsSvc.register({
		id: "resource.create.subdirectory",
		label: "Create a subdirectory",
		args: [
			{
				label: "Parent resource ID",
				isOptional: false,
				schema: {
					type: "string"
				}
			}
		],
		returns: "none",
		handler: async (args) => {
			const resource = resourceSvc.getResourceDescriptorById(args[0] as string);

			const resourceBasePath = resource?.basePath || "";
			const resourceName = resource?.name || "";

			const basePath = normalizeResourcePath(resourceBasePath, resourceName);

			const res = await dialogSvc.open(InputDialog, "resource.createSubDirectory", {
				title: terms.dialog.createDirectory.title,
				description: terms.dialog.createDirectory.controls.name.label,
				confirmLabel: terms.dialog.createDirectory.buttonConfirm,
				cancelLabel: terms.dialog.createDirectory.buttonCancel,
				validators: [
					ValidatorString({ required: true }),
					(name) => {
						if (name?.includes("/")) {
							return [
								{
									type: SCHEMA_VALIDATION_ERROR_TYPE.PATTERN,
									message: terms.dialog.createDirectory.messages.pattern,
									metaData: {}
								}
							];
						}

						const fullPath = normalizeResourcePath(basePath, name);
						if (resourceSvc.getResourceByPath(fullPath)) {
							return [
								{
									type: SCHEMA_VALIDATION_ERROR_TYPE.DUPLICATE_IDENTIFIER,
									message: terms.dialog.createDirectory.messages.duplicateIdentifier,
									metaData: {}
								}
							];
						}

						return [];
					}
				]
			});

			const name = res?.value;

			if (!name) {
				return;
			}

			const loadingToast = toastSvc.add({
				type: TOAST_TYPE.LOADING,
				message: termsEditorViewEditor.toasts.creatingDirectory,
				isClosable: false
			});

			try {
				await resourceSvc.createDirectory(name, basePath);
			} catch (err) {
				// @TODO: show different messages for different errors
				const errorTracker = ErrorTracker.getInstance();
				errorTracker.captureException(err);

				toastSvc.add({
					type: TOAST_TYPE.ERROR,
					message: termsEditorCommon.toasts.createDirectory.error.unknown,
					details: String(err)
				});
			} finally {
				toastSvc.remove(loadingToast);
			}

			return;
		}
	});

	commandsSvc.register({
		id: "resource.open",
		label: "Open Resource",
		args: [
			{
				label: "Resource ID",
				isOptional: false,
				schema: {
					type: "string"
				}
			}
		],
		returns: "none",
		handler: async (args) => {
			const resource = resourceSvc.getResourceDescriptorById(args[0] as string);

			if (!resource) {
				throw new Error("Resource not found.");
			}

			const viewName = resourceSvc.getResourceView(resource.resourceType);

			if (!viewName) {
				throw new Error(`No view associated with resource type '${resource.resourceType}'.`);
			}

			viewSvc.open(
				viewName,
				"resource:" + args[0],
				{
					resourceId: args[0]
				},
				true
			);
		}
	});

	commandsSvc.register({
		id: "resource.dialog.open",
		label: "Open Resource Dialog",
		args: [],
		returns: "none",
		handler: async () => {
			dialogSvc.open(ResourceListDialog, "resourceList", {
				title: "Open Resource",
				icon: "mdi/folder-open"
			});
		}
	});

	commandsSvc.register({
		id: "resource.rename",
		label: "Rename Resource",
		args: [
			{
				label: "Resource ID",
				isOptional: false,
				schema: {
					type: "string"
				}
			}
		],
		returns: "none",
		handler: async (args) => {
			const resourceId = args[0] as string;
			const getResource = () => resourceSvc.getResourceDescriptorById(resourceId);

			const resource = getResource();

			if (!resource) {
				throw new Error("Resource not found.");
			}

			const result = await dialogSvc.open(RenameDialog, "resource.rename", {
				resourceName: resource.name
			});

			const latestResource = getResource();

			if (!result.newName) {
				// User didn't set new name
				return;
			}

			if (latestResource.name === result.newName) {
				// Name is the same or already changed
				return;
			}

			if (latestResource.name !== resource.name) {
				// Name was changed while user was changing it

				toastSvc.add({
					type: TOAST_TYPE.ERROR,
					message: termsEditorCommon.toasts.resourceRename.error.alreadyRenamed
				});
				return;
			}

			const loadingToast = toastSvc.add({
				type: TOAST_TYPE.LOADING,
				message: termsEditorViewEditor.toasts.savingView,
				isClosable: false
			});

			try {
				await resourceSvc.renameResource(resourceId, result.newName);
			} catch (err) {
				// @TODO: show different messages for different errors
				const errorTracker = ErrorTracker.getInstance();
				errorTracker.captureException(err);

				toastSvc.add({
					type: TOAST_TYPE.ERROR,
					message: termsEditorCommon.toasts.resourceRename.error.unknown,
					details: String(err)
				});
			} finally {
				toastSvc.remove(loadingToast);
			}

			return;
		}
	});

	commandsSvc.register({
		id: "resource.move",
		label: "Move Resource",
		args: [
			{
				label: "Moved Resource ID",
				isOptional: false,
				schema: {
					type: "string"
				}
			},
			{
				label: "Target Directory Resource ID",
				isOptional: false,
				schema: {
					type: "string"
				}
			}
		],
		returns: "none",
		handler: async (args) => {
			const resourceId = args[0] as string;
			const targetId = args[1] as string;
			const getResource = () => resourceSvc.getResourceDescriptorById(resourceId);

			const resource = getResource();

			if (!resource) {
				throw new Error("Resource not found.");
			}

			if (!targetId) {
				// User didn't select target resource
				return;
			}

			const targetIsRoot = targetId === "__root__";

			if (!targetIsRoot) {
				const targetResource = resourceSvc.getResourceById(targetId);

				if (!targetResource) {
					// Target resource doesn't exist

					toastSvc.add({
						type: TOAST_TYPE.ERROR,
						message: termsEditorCommon.toasts.resourceMove.error.targetNotFound
					});
					return;
				}

				if (!resourceSvc.isDirectoryResource(targetResource)) {
					// Target resource is not a directory

					toastSvc.add({
						type: TOAST_TYPE.ERROR,
						message: termsEditorCommon.toasts.resourceMove.error.invalidTargetType
					});
					return;
				}
			}

			const loadingToast = toastSvc.add({
				type: TOAST_TYPE.LOADING,
				message: termsEditorViewEditor.toasts.movingResource,
				isClosable: false
			});

			try {
				await resourceSvc.moveResource(resourceId, targetIsRoot ? "" : targetId);
			} catch (err) {
				// @TODO: show different messages for different errors
				const errorTracker = ErrorTracker.getInstance();
				errorTracker.captureException(err);

				toastSvc.add({
					type: TOAST_TYPE.ERROR,
					message: termsEditorCommon.toasts.resourceMove.error.unknown,
					details: String(err)
				});
			} finally {
				toastSvc.remove(loadingToast);
			}

			return;
		}
	});

	commandsSvc.register({
		id: "resource.moveDialog.open",
		label: "Open Move Resource Dialog",
		args: [
			{
				label: "Resource ID",
				isOptional: false,
				schema: {
					type: "string"
				}
			}
		],
		returns: "none",
		handler: async (args) => {
			const resourceId = args[0] as string;
			const resource = resourceSvc.getResourceDescriptorById(resourceId);

			if (!resource) {
				throw new Error("Resource not found.");
			}

			//TADY JE MOVE

			const result = await dialogSvc.open(ResourceListDialog, "resource.move", {
				title: "Move",
				icon: "mdi/folder-move",
				description: `Move resource '${resource.basePath}/${resource.name}' to directory:`,
				allowedResourceTypes: [ RESOURCE_TYPES.DIRECTORY ],
				isDisabled: (node) => {
					return normalizeResourcePath(node.basePath, node.name) === resource.basePath;
				}
			});

			const targetId = result?.resourceId;

			if (!targetId) {
				// User didn't select target resource
				return;
			}

			return commandsSvc.execute("resource.move", resourceId, targetId);
		}
	});

	commandsSvc.register({
		id: "resource.duplicate",
		label: "Duplicate Resource",
		args: [
			{
				label: "Resource ID",
				isOptional: false,
				schema: {
					type: "string"
				}
			},
			{
				label: "Open Duplicated Resource",
				isOptional: true,
				schema: {
					type: "boolean"
				}
			}
		],
		returns: "none",
		handler: async (args) => {
			const resourceId = args[0] as string;
			const openDuplicatedResource = args[1] as boolean;
			const resource = resourceSvc.getResourceDescriptorById(resourceId);

			if (!resource) {
				throw new Error("Resource not found.");
			}

			// @todo Show dialog, process rename

			const userInput = await dialogSvc.open(InputDialog, "resource.duplicate", {
				defaultValue: "Copy of " + resource.name,
				title: terms.dialog.resourceDuplicate.title,
				description: terms.dialog.resourceDuplicate.description,
				confirmLabel: terms.dialog.resourceDuplicate.buttonConfirm,
				cancelLabel: terms.dialog.resourceDuplicate.buttonCancel,
				validators: [
					ValidatorString({ required: true }),
					(value) => {
						if (resourceSvc.getResourceByPath(normalizeResourcePath(resource.basePath, value))) {
							return [
								{
									type: SCHEMA_VALIDATION_ERROR_TYPE.DUPLICATE_IDENTIFIER,
									message: terms.dialog.resourceRename.messages.name.duplicateIdentifier,
									metaData: {}
								}
							];
						} else {
							return [];
						}
					}
				]
			});

			if (!userInput.value) {
				// User didn't set new name
				return;
			}

			const loadingToast = toastSvc.add({
				type: TOAST_TYPE.LOADING,
				message: termsEditorViewEditor.toasts.savingView,
				isClosable: false
			});

			try {
				const duplicatedResourceId = generateId();
				await resourceSvc.duplicateResource(resourceId, userInput.value, duplicatedResourceId);

				if (openDuplicatedResource) {
					const viewName = resourceSvc.getResourceView(resource.resourceType);

					if (viewName) {
						viewSvc.open(
							viewName,
							"resource:" + duplicatedResourceId,
							{
								resourceId: duplicatedResourceId
							},
							true
						);
					}
				}
			} catch (err) {
				const errorTracker = ErrorTracker.getInstance();
				errorTracker.captureException(err);

				toastSvc.add({
					type: TOAST_TYPE.ERROR,
					// TODO: translate
					message: "Duplicating resource failed.",
					details: String(err)
				});
			} finally {
				toastSvc.remove(loadingToast);
			}
		}
	});

	commandsSvc.register({
		id: "resource.export",
		label: "Export Resource",
		args: [
			{
				label: "Resource ID",
				isOptional: false,
				schema: {
					type: "string"
				}
			}
		],
		returns: "none",
		handler: async (args) => {
			try {
				const resourceId = args[0] as string;
				const blueprint = await resourceSvc.getBlueprintById(resourceId);

				const content = blueprint.content;
				const name = blueprint.descriptor.name;

				const element = document.createElement("a");
				const file = new Blob([ content ], { type: "text/plain" });
				element.href = URL.createObjectURL(file);
				element.download = name + ".yaml";
				element.click();
			} catch (err) {
				const errorTracker = ErrorTracker.getInstance();
				errorTracker.captureException(err);

				toastSvc.add({
					type: TOAST_TYPE.ERROR,
					// @todo Translate
					message: "Failed to download.",
					details: String(err)
				});
			}
		}
	});

	commandsSvc.register({
		id: "resource.delete",
		label: "Delete Resource",
		args: [
			{
				label: "Resource ID",
				isOptional: false,
				schema: {
					type: "string"
				}
			}
		],
		returns: "none",
		handler: async (args) => {
			const resource = resourceSvc.getResourceDescriptorById(args[0] as string);

			if (!resource) {
				throw new Error("Resource not found.");
			}

			await dialogSvc.open(ConfirmationDialog, "resource.delete", {
				title: termsEditorCommon.dialog.resourceDelete.title,
				message: getTermByKey(
					termsEditorCommon.dialog.resourceDelete.message,
					resource.resourceType,
					"resource"
				),
				translateVars: {
					name: resource.name
				},
				icon: "mdi/alert",
				confirmLabel: termsEditorCommon.dialog.confirmation.buttonDelete,
				confirmIcon: "mdi/delete",
				confirmStyle: ACTION_BUTTON_STYLE.DANGER,
				doConfirm: async () => {
					const toastId = toastSvc.add({
						type: TOAST_TYPE.LOADING,
						message: "Deleting resource...",
						isClosable: false
					});

					try {
						await resourceSvc.deleteResource(resource.id);

						toastSvc.update(toastId, {
							type: TOAST_TYPE.SUCCESS,
							message: getTermByKey(
								termsEditorCommon.toasts.resourceDelete,
								resource.resourceType,
								"resource"
							).success,
							isClosable: true,
							timeoutMs: 3000
						});
					} catch (err) {
						const errorTracker = ErrorTracker.getInstance();
						errorTracker.captureException(err);

						toastSvc.update(toastId, {
							type: TOAST_TYPE.ERROR,
							message: getTermByKey(
								termsEditorCommon.toasts.resourceDelete,
								resource.resourceType,
								"resource"
							).error,
							isClosable: true,
							details: String(err)
						});
					}
				}
			});
		}
	});

	commandsSvc.register({
		id: "iframe.open",
		label: "Open URL in tab",
		args: [
			{
				label: "URL",
				isOptional: false,
				schema: {
					type: "string"
				}
			},
			{
				label: "Label",
				isOptional: false,
				schema: {
					type: "string"
				}
			},
			{
				label: "Icon",
				isOptional: true,
				schema: {
					type: "string"
				}
			}
		],
		returns: "none",
		handler: async (args) => {
			viewSvc.open(
				"iframe",
				"iframe:" + args[0],
				{
					url: args[0],
					label: args[1],
					icon: args[2]
				},
				true
			);
		}
	});

	commandsSvc.register({
		id: "view.save",
		label: "Save current view",
		args: [],
		returns: "none",
		handler: async () => {
			const view = viewSvc.getCurrentView();

			if (isViewEditable(view)) {
				const savingToastId = toastSvc.add({
					type: TOAST_TYPE.LOADING,
					message: termsEditorViewEditor.toasts.savingView,
					isClosable: false
				});

				try {
					await view.save();

					toastSvc.update(savingToastId, {
						type: TOAST_TYPE.SUCCESS,
						message: termsEditorViewEditor.toasts.viewHasBeenSaved,
						timeoutMs: 2500,
						isClosable: true
					});
				} catch (err) {
					const errorTracker = ErrorTracker.getInstance();
					errorTracker.captureException(err);

					toastSvc.update(savingToastId, {
						type: TOAST_TYPE.ERROR,
						message: termsEditorViewEditor.toasts.failedToSaveView,
						details: String(err),
						isClosable: true
					});
				}
			}
		}
	});

	commandsSvc.register({
		id: "views.saveAll",
		label: "Save all edited views",
		args: [],
		returns: "none",
		handler: async () => {
			const savingToastId = toastSvc.add({
				type: TOAST_TYPE.LOADING,
				message: termsEditorViewEditor.toasts.savingAllViews,
				isClosable: false
			});

			try {
				await viewSvc.saveAllViews();

				toastSvc.update(savingToastId, {
					type: TOAST_TYPE.SUCCESS,
					message: termsEditorViewEditor.toasts.viewHasBeenSaved,
					timeoutMs: 2500,
					isClosable: true
				});
			} catch (err) {
				const errorTracker = ErrorTracker.getInstance();
				errorTracker.captureException(err);

				toastSvc.update(savingToastId, {
					type: TOAST_TYPE.ERROR,
					message: termsEditorViewEditor.toasts.failedToSaveView,
					details: String(err),
					isClosable: true
				});
			}
		}
	});

	commandsSvc.register({
		id: "envErrors.open",
		label: "Open Errors log in tab",
		args: [],
		returns: "none",
		handler: async (args) => {
			viewSvc.open(
				"errorLog",
				"errorLog",
				{
					url: args[0],
					label: args[1],
					icon: args[2]
				},
				true
			);
		}
	});

	commandsSvc.register({
		id: "release.openDialog",
		label: "Open Release Dialog",
		args: [],
		returns: "none",
		handler: async () => {
			dialogSvc.open(ReleaseDialog, "release", { message: "" });
		}
	});

	commandsSvc.register({
		id: "deployment.openDialog",
		label: "Open Deployment Dialog",
		args: [],
		returns: "none",
		handler: async () => {
			// dialogSvc.open(DeployDialog, "deploy", { message: "Test" });
		}
	});

	commandsSvc.register({
		id: "preview.open",
		label: "Open App Preview",
		args: [],
		returns: "none",
		handler: async () => {
			dialogSvc.open(AppPreview, "app-preview", {});
		}
	});

	commandsSvc.register({
		id: "environment.autloginAppUser",
		label: "Auto-login app user",
		args: [],
		returns: "none",
		handler: async () => {
			const environment = dc.get("environmentService");

			const toastId = toastSvc.add({
				type: TOAST_TYPE.LOADING,
				message: "Executing Auto-Login...",
				isClosable: false
			});
			
			try {
				await environment.autologinAppUser();

				toastSvc.update(toastId, {
					type: TOAST_TYPE.SUCCESS,
					message: "Auto-Login executed successfully.",
					isClosable: true,
					timeoutMs: 3000
				});
			} catch (err) {
				if (err instanceof ApiError && err.name === API_ERROR_NAME.FEATURE_NOT_AVAILABLE) {
					toastSvc.update(toastId, {
						type: TOAST_TYPE.ERROR,
						message: "Auto-login feature is not configure in app settings.",
						isClosable: true,
					});
				} else {
					toastSvc.update(toastId, {
						type: TOAST_TYPE.ERROR,
						message: "Auto-login failed.",
						details: String(err),
						isClosable: true,
					});
				}
			}
		}
	});

	commandsSvc.register({
		id: "view.open",
		label: "Open View",
		args: [
			{
				label: "View Name",
				isOptional: false,
				schema: {
					type: "string"
				}
			},
			{
				label: "View ID",
				isOptional: false,
				schema: {
					type: "string"
				}
			}
		],
		returns: "none",
		handler: async (args) => {
			const viewName = args[0] as string;
			const viewId = args[1] as string;

			viewSvc.open(viewName, viewId, null, true);
		}
	});

	// Import Blueprint open dialog
	commandsSvc.register({
		id: "importBlueprint.open",
		label: "Import Blueprint",
		args: [
			{
				label: "Resource ID",
				isOptional: true,
				schema: {
					type: "string"
				}
			}
		],
		returns: "none",
		handler: async (args) => {
			const resourceId = args[0] as string;
			// const blueprint = await resourceSvc.getBlueprintById(resourceId);
			dialogSvc.open(ImportBlueprintDialog, "import", { resourceId });
		}
	});

	commandsSvc.register({
		id: "routesEditor.open",
		label: "Open routes editor",
		args: [],
		returns: "none",
		handler: async () => {
			const routeResources = resourceSvc.getResourceListByType(RESOURCE_TYPES.ROUTES);
			const routeRes = routeResources[0];

			if (!routeRes) {
				console.error("Route resource not found.");
				return;
			}

			commandsSvc.execute("resource.open", routeRes.id).catch((err) => {
				console.warn("Failed to open routes editor:", err);
			});
		}
	});

	if (productSupportSvc.hasBugReportLink()) {
		commandsSvc.register({
			id: "bugReport.send",
			label: "Report a bug",
			args: [],
			returns: "none",
			handler: async () => {
				window.open(productSupportSvc.getBugReportLink(), '_blank');
			}
		});
	}

	commandsSvc.register({
		id: "server.reload",
		label: "Reload server",
		args: [],
		returns: "none",
		handler: async () => {
			await dialogSvc.open(ConfirmationDialog, "server.reload.confirm", {
				title: "Reload Server",
				message: "This will reload all blueprints and configurations. It might take a while. Do you wish to continue?",
				icon: "mdi/alert",
				confirmLabel: "Reload Server",
				confirmIcon: "mdi/reload-alert",
				confirmStyle: ACTION_BUTTON_STYLE.DANGER,
				doConfirm: async () => {
					const toastId = toastSvc.add({
						type: TOAST_TYPE.LOADING,
						message: "Reloading server...",
						isClosable: false
					});

					try {
						await apiSvc.reloadServer();

						toastSvc.update(toastId, {
							type: TOAST_TYPE.SUCCESS,
							message: "Server reloaded successfully.",
							isClosable: true,
							timeoutMs: 3000
						});
					} catch (err) {
						toastSvc.update(toastId, {
							type: TOAST_TYPE.ERROR,
							message: "Failed to reload server.",
							isClosable: true,
							details: String(err)
						});
					}
				}
			});
		}
	});
}
